import { Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '阅读体验至上',
    desc: '从高清原图到夜间模式，我们把每一个阅读细节都当作核心工作，只为让读者看得清晰、看得舒适。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '尊重原创内容',
    desc: '坚持内容原则，审慎推荐优质连载作品，尊重创作者劳动，营造健康有序的阅读环境。',
  },
  {
    icon: 'ri-device-line',
    title: '全场景适配',
    desc: '无论是手机、平板还是桌面，都提供一致的优质体验，让阅读不因设备而受限。',
  },
  {
    icon: 'ri-chat-3-line',
    title: '倾听读者声音',
    desc: '持续收集读者反馈，不断迭代阅读功能，让漫蛙成为越来越懂你的阅读伙伴。',
  },
];

export default function About() {
  useSeo({
    title: '关于漫蛙漫画网 - 平台定位、发展理念与内容原则',
    description: '了解漫蛙漫画网的平台定位与发展理念，我们专注优化漫画阅读体验，坚持阅读体验至上、尊重原创内容、全场景适配与倾听读者声音。',
    canonicalPath: '/about',
  });
  return (
    <div className="bg-background-50">
      {/* Page header */}
      <section className="relative overflow-hidden bg-background-950">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20illustration%20of%20a%20bookshelf%20with%20rows%20of%20colorful%20manga%20tankoubon%20books%2C%20warm%20amber%20lighting%2C%20dark%20charcoal%20background%2C%20cozy%20reading%20corner%2C%20bold%20comic%20art%20style%2C%20high%20detail%20digital%20painting&width=1600&height=700&seq=about-hero&orientation=landscape"
          alt="漫蛙漫画网关于我们封面插画"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-background-950"></div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-24 md:px-6 md:py-32">
          <nav aria-label="面包屑" className="text-xs text-background-300">
            <ol className="flex items-center gap-1.5">
              <li><a href="/" className="hover:text-primary-300">首页</a></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page">关于我们</li>
            </ol>
          </nav>
          <h1 className="mt-5 max-w-2xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            关于漫蛙漫画网
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            漫蛙漫画网是一个专注于优化漫画阅读体验的在线阅读平台，从高清原图、双页浏览到夜间模式，
            我们把阅读的每一个细节都做到极致，让每一格画面都清晰、舒适、沉浸。
          </p>
        </div>
      </section>

      {/* Intro / positioning */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">平台定位</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              我们相信，好的阅读体验值得被认真打磨
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              漫画不只是故事，也是画面、节奏与情绪的艺术。漫蛙漫画网从诞生之初就把“阅读体验”当作立身之本——
              我们深入研究不同设备、不同屏幕与不同光线条件下的阅读痛点，推出高清原图、双页浏览、夜间模式、
              缩放设置与滚动阅读五种核心阅读模式，让读者把注意力真正放回故事本身。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              目前平台已收录超过三千六百部连载漫画，覆盖热血、科幻、玄幻、悬疑、恋爱与日常等主流类型，
              累计阅读量突破一亿，服务着数以万计的忠实读者。
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20creative%20studio%20desk%20with%20drawing%20tablets%20sketchbooks%20and%20manga%20volumes%2C%20warm%20natural%20lighting%2C%20artistic%20workspace%2C%20cream%20and%20amber%20tones%2C%20editorial%20photography%2C%20high%20detail&width=1200&height=800&seq=about-team&orientation=landscape"
                alt="漫蛙漫画网创作与产品团队工作场景"
                className="h-auto w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent-600">发展理念</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">我们坚持的四件事</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500/15 text-xl text-accent-600">
                  <i className={v.icon}></i>
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content principles */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">内容原则</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">用心推荐每一部作品</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              我们以严谨的态度筛选与推荐连载作品，既尊重创作者，也尊重读者，让推荐机制透明、可信、可持续。
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-5 sm:grid-cols-2">
              {[
                ['ri-sparkling-2-line', '精选推荐', '结合热度、口碑与题材多样性进行审慎推荐，避免一味追逐流量。'],
                ['ri-copyright-line', '尊重版权', '坚持正版内容与授权合作，尊重每一位创作者的劳动成果。'],
                ['ri-error-warning-line', '审慎审核', '对内容进行规范审核，营造健康、友善、可持续的阅读环境。'],
                ['ri-refresh-line', '持续迭代', '根据读者反馈持续优化阅读功能与内容结构，保持平台生命力。'],
              ].map(([icon, title, desc]) => (
                <div key={title} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-500/15 text-lg text-primary-600">
                    <i className={icon as string}></i>
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground-950">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent-600">联系我们</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">期待听到你的声音</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              无论是功能建议、内容反馈还是合作洽谈，都欢迎随时联系我们。你的每一条建议，都是漫蛙变得更好的动力。
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="mailto:hello@manwa-comic.example"
                className="inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
              >
                <i className="ri-mail-line"></i>
                hello@manwa-comic.example
              </a>
              <Link
                to="/faq"
                className="inline-flex whitespace-nowrap items-center gap-2 rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-50"
              >
                <i className="ri-question-line"></i>
                查看常见问题
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}